<template>
  <v-form ref="form" v-model="isFormValid" @submit.prevent="onSubmit">
    <div class="text-h6 text-center font-weight-black">Account Information</div>
    <v-sheet class="border-opacity-25 overflow-hidden border-thin rounded-lg pa-5">
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field
            v-model="formModel.email_address"
            label="Email"
            autocomplete="email"
            :rules="[
              rules.required,
              rules.email,
              rules.emailUnique(formModel.email_address, props.user?.id),
            ]"
            :error-messages="errors.email_address"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="formModel.first_name"
            label="First Name"
            :rules="[rules.required]"
            :error-messages="errors.first_name"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="formModel.last_name"
            label="Last Name"
            :rules="[rules.required]"
            :error-messages="errors.last_name"></v-text-field>
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12" md="12">
          <v-select
            v-model="formModel.role"
            label="Role"
            :items="roles"
            item-value="id"
            item-title="role_name"
            :rules="[rules.required]"></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field
            v-model="formattedBirthday"
            label="Birth Date"
            type="date"
            :rules="birthdayRules"
            required></v-text-field>
        </v-col>
      </v-row>
    </v-sheet>
    <div v-if="isCreate" class="text-h6 text-center mt-8 font-weight-black">Password</div>
    <v-sheet v-if="isCreate" class="border-opacity-25 border-thin rounded-lg overflow-hidden pa-5">
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="formModel.password"
            label="Password"
            autocomplete="current-password"
            :rules="[rules.required, rules.passwordComplexity]"
            :append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showpassword ? 'text' : 'password'"
            :error-messages="errors.password"
            @click:append="showpassword = !showpassword"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="confirmPassword"
            label="Confirm Password"
            :rules="[
              rules.required,
              rules.passwordMatch(formModel.password || '', confirmPassword),
            ]"
            :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showConfirmPassword ? 'text' : 'password'"
            @click:append="showConfirmPassword = !showConfirmPassword"></v-text-field>
          <div class="invalid-feedback">{{ errors.password }}</div>
        </v-col>
      </v-row>
    </v-sheet>
    <div class="text-h6 text-center mt-8 font-weight-black">Contact Information</div>
    <v-sheet class="border-opacity-25 overflow-hidden border-thin rounded-lg pa-5">
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field
            v-model="formModel.phone_number"
            label="Phone Number"
            :rules="[rules.required, rules.phone]"
            :error-messages="errors.phone_number"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <CountrySelect
            v-model="formModel.country"
            label="Country"
            :rules="[rules.required]"
            :countryName="true"
            :error-messages="errors.country"></CountrySelect>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field
            v-model="formModel.street_address"
            label="Street Address"
            :rules="[rules.required]"
            :error-messages="errors.street_address"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field
            v-model="formModel.city"
            label="City"
            :rules="[rules.required]"
            :error-messages="errors.city"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <RegionSelect
            v-model="formModel.state"
            label="State"
            :rules="[rules.required]"
            :country="formModel.country"
            :countryName="true"
            :error-messages="errors.state"></RegionSelect>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field
            v-model="formModel.postal_code"
            label="Postal Code"
            :rules="[rules.required, rules.postalCode]"
            :error-messages="errors.postal_code"></v-text-field>
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12">
          <v-checkbox
            label="Force Password Reset"
            :model-value="formModel.force_reset"
            @update:modelValue="updateForceReset"></v-checkbox>
        </v-col>
      </v-row>
    </v-sheet>
    <v-row>
      <v-col>
        <v-btn
          color="primary"
          block
          class="rounded-lg mb-8 mt-8"
          elevation="2"
          :disabled="!isFormValid"
          :loading="isSubmitting"
          @click="onSubmit">
          {{ buttonText }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, toRef, watch } from 'vue';
import { IUserDTO, UserDTO } from '@/dtos';
import { formatDateFieldInput, rules } from '@/helpers';
import CountrySelect from '@/components/ui/forms/countryregion/country-select.vue';
import RegionSelect from '@/components/ui/forms/countryregion/region-select.vue';
import type { VForm } from 'vuetify/components';
import { useUpdateStatusStore, useUserStore } from '@/stores/modules';
import { SNACKSTATUS } from '@/stores/util';

interface Props {
  user?: IUserDTO;
  isCreate?: boolean;
  isAdmin?: boolean;
}

const props = defineProps<Props>();
const userStore = useUserStore();
const statusStore = useUpdateStatusStore();
const isAdmin = toRef(props, 'isAdmin', false);
const isCreate = toRef(props, 'isCreate', false);
const roles = computed(() => userStore.getRolesQuery.data?.data);
const emit = defineEmits(['profileSaved', 'snack']);
const isFormValid = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>();
const formModel = ref<IUserDTO>(new UserDTO());
const isSubmitting = ref<boolean>(false);
const confirmPassword = ref<string>('');
const showpassword = ref<boolean>(false);
const showConfirmPassword = ref<boolean>(false);
const birthdayRules = computed(() => {
  if (!isAdmin.value || (formModel.value.role && formModel.value.role.role_name == 'User')) {
    return [rules.required, rules.birthday];
  } else {
    return [];
  }
});
const errors = ref({
  first_name: '',
  last_name: '',
  email_address: '',
  birthday: '',
  password: '',
  phone_number: '',
  country: '',
  street_address: '',
  city: '',
  state: '',
  postal_code: '',
  apiError: '',
});
watch(
  () => statusStore.getStatus('user-create'),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = 'user-create';
    prepEmit(value, key);
  },
);

watch(
  () => statusStore.getStatus(`user-${formModel.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `user-${formModel.value.id}`;
    prepEmit(value, key);
  },
);

const prepEmit = (value: SNACKSTATUS, key: string) => {
  emit('snack', 'User', value, statusStore.getError(key));
  if (value.startsWith('success')) {
    emit('profileSaved', true);
  } else if (value == 'error') {
    emit('profileSaved', false);
  }
  statusStore.clearStatus(key);
};

const buttonText = computed(() => {
  let value = 'Save';
  if (isCreate.value) {
    if (isAdmin.value) {
      value = 'Create User';
    } else {
      value = 'Register';
    }
  }
  return value;
});

const formattedBirthday = computed({
  get: () => {
    return formatDateFieldInput(formModel.value.birthday);
  },
  set: (value: Date) => {
    formModel.value.birthday = new Date(value + 'T00:00:00');
  },
});

// Form submission handler
const onSubmit = async () => {
  if (form.value && form.value.isValid) {
    if (isCreate.value) {
      userStore.create.mutateAsync(formModel.value);
    } else {
      userStore.update.mutateAsync(formModel.value);
    }
    isSubmitting.value = false;
  }
};
watch(
  () => props.user,
  () => {
    formModel.value = new UserDTO(props.user);
  },
  { immediate: true },
);

const updateForceReset = (value: boolean | null) => {
  formModel.value.force_reset = value ?? false;
};

onMounted(() => {
  if (formModel.value.country == '') {
    formModel.value.country = 'United States';
    formModel.value.state = 'Maryland';
  }
});
</script>
